import { StandardEvents } from '@shopify/events';

// Read-only presentation: never edits a cart, shipping rate or destination.
export function shippingMessage(context, cart, variant = 'bar') {
  const t = (key, values) => window.GlintI18n.t('shipping.' + key, values);
  const country = context.countryName || context.countryCode || t('destination');
  const known = context.verified === true && context.currency === 'EUR' && cart?.currency === 'EUR';
  const total = cart?.total;
  const ready = Number.isFinite(total) && total >= 0 && Number.isFinite(cart?.count) && cart.count >= 0;
  const amount = (cents) => new Intl.NumberFormat(context.locale || window.GlintI18n.locale || 'en-IE', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: cents % 100 ? 2 : 0
  }).format(cents / 100);
  if (!known || !ready) return {
    state: 'unknown', text: variant === 'bar' ? t('options') : t('unknown_detail', { country }), progress: null
  };
  const threshold = context.threshold;
  const rate = context.standardRate;
  if (!Number.isFinite(rate) || rate < 0) return { state: 'unknown', text: t('checkout_short'), progress: null };
  if (!Number.isFinite(threshold) || threshold <= 0) return {
    state: 'fixed', text: variant === 'bar' ? t('fixed_bar', { rate: amount(rate) }) : t('fixed_detail', { country, rate: amount(rate) }), progress: null
  };
  if (cart.pendingGifts) return {
    state: 'pending', progress: null,
    text: variant === 'bar' ? t('from', { threshold: amount(threshold) }) : t('rates', { country, rate: amount(rate), threshold: amount(threshold) }) + ' ' + t('pending')
  };
  const remaining = Math.max(0, threshold - total);
  const unlocked = cart.count > 0 && remaining === 0;
  const progress = cart.count > 0 ? Math.min(100, total * 100 / threshold) : null;
  if (variant === 'detail') return {
    state: unlocked ? 'unlocked' : cart.count > 0 ? 'progress' : 'empty', progress,
    text: t('rates', { country, rate: amount(rate), threshold: amount(threshold) }) + ' ' +
      (unlocked ? t('reached') : cart.count > 0 ? t('remaining_detail', { amount: amount(remaining) }) : t('empty_detail'))
  };
  let text = unlocked ? t('unlocked') : cart.count > 0 ? t('remaining', { amount: amount(remaining) }) : t('from', { threshold: amount(threshold) });
  // A tiny shortfall should not imply that an imaginary €2 item exists.
  // The bundle builder offers an actual eligible piece, its price and gains.
  if (!unlocked && cart.count > 0 && remaining < 1000) text = t('from', { threshold: amount(threshold) });
  if (variant === 'cart') text = `${country} · ${text}`;
  return { state: unlocked ? 'unlocked' : cart.count > 0 ? 'progress' : 'empty', text, progress };
}

const cartReads = new WeakMap();
function readCart(event) {
  if (!cartReads.has(event)) {
    const root = window.Shopify?.routes?.root || '/';
    cartReads.set(event, fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then((response) => { if (!response.ok) throw new Error('Cart unavailable'); return response.json(); }));
  }
  return cartReads.get(event);
}

export function ajaxCart(cart) {
  return {
    total: cart?.total_price == null ? NaN : Number(cart.total_price),
    count: cart?.item_count == null ? NaN : Number(cart.item_count),
    currency: cart?.currency,
    pendingGifts: (cart?.items || []).some((item) =>
      (item.properties?._glint_bundle_role === 'free' || item.handle === 'glint-complimentary-30-40') && Number(item.final_line_price) > 0)
  };
}

export class GlintShippingMessage extends HTMLElement {
  #context;
  #revision = 0;
  connectedCallback() {
    try { this.#context = JSON.parse(this.querySelector('[data-shipping-context]')?.textContent || '{}'); }
    catch { this.#context = {}; }
    this.#render({ total: this.#context.cartTotal, count: this.#context.cartCount, currency: this.#context.cartCurrency, pendingGifts: this.#context.cartPendingGifts });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#update);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#update);
    document.addEventListener('glint:cart-refreshed', this.#legacyUpdate);
    window.addEventListener('pageshow', this.#pageShow);
  }
  disconnectedCallback() {
    this.#revision++;
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#update);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#update);
    document.removeEventListener('glint:cart-refreshed', this.#legacyUpdate);
    window.removeEventListener('pageshow', this.#pageShow);
  }
  #render(cart) {
    const view = shippingMessage(this.#context, cart, this.getAttribute('variant') || 'bar');
    const text = this.querySelector('[data-shipping-text]');
    if (text && text.textContent !== view.text) text.textContent = view.text;
    this.dataset.shippingState = view.state;
    const track = this.querySelector('[data-shipping-track]');
    const fill = this.querySelector('[data-shipping-fill]');
    if (track) track.hidden = view.progress === null;
    if (fill) fill.style.width = `${view.progress ?? 0}%`;
  }
  #recover = async (revision, event) => {
    if (!this.isConnected || revision !== this.#revision) return;
    try {
      const cart = await readCart(event);
      if (this.isConnected && revision === this.#revision) this.#render(ajaxCart(cart));
    } catch {
      if (this.isConnected && revision === this.#revision) this.#render(null);
    }
  };
  #update = async (event) => {
    const revision = ++this.#revision;
    try {
      await event.promise;
      if (!this.isConnected || revision !== this.#revision) return;
      // One shared read per event checks both the discounted total and whether
      // native FREE lines are actually free. Different events never share a
      // stale request; a later cart change always wins.
      await this.#recover(revision, event);
    } catch { await this.#recover(revision, event); }
  };
  #legacyUpdate = (event) => {
    this.#revision++;
    if (event.detail?.cart) this.#render(ajaxCart(event.detail.cart));
    else this.#recover(this.#revision, event);
  };
  #pageShow = (event) => {
    if (event.persisted) this.#recover(++this.#revision, event);
  };
}

if (!customElements.get('glint-shipping-message')) customElements.define('glint-shipping-message', GlintShippingMessage);
